import { useApi } from '@gravitate-js/the-armory'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getAPIURL } from '@utils/api'
import type { AutoDispatchBuildingResponse, AutoDispatchOverviewResponse } from './response'

export const useAutoDispatch = () => {
  const api = useApi({ baseURLOverride: getAPIURL() })
  const queryClient = useQueryClient()

  const useAutoDispatchOverview = () =>
    useQuery<AutoDispatchOverviewResponse[]>({
      queryKey: ['autoDispatchOverview'],
      queryFn: () => api.post('auto_dispatch/overview'),
    })

  const useAutoDispatchDetails = (id: string) => {
    return useQuery({
      queryKey: ['autoDispatchDetails', id],
      queryFn: () => api.post('auto_dispatch/details', { id }),
      enabled: !!id,
    })
  }

  const useAutoDispatchBuildingDetails = (id: string) => {
    return useQuery<AutoDispatchBuildingResponse>({
      queryKey: ['autoDispatchDetails', id],
      queryFn: () => api.post('auto_dispatch/building/details', { id }),

      enabled: !!id,
    })
  }

  const useScheduleLock = useMutation({
    mutationKey: ['scheduleLock'],
    mutationFn: (payload: { ids: string[]; run_id: string }) =>
      api.post('auto_dispatch/lock_schedules', payload.ids, {
        queryParams: new URLSearchParams({ run_id: payload.run_id }),
      }),
  })

  const useScheduleUnlock = useMutation({
    mutationKey: ['scheduleUnlock'],
    mutationFn: (payload: { ids: string[]; run_id: string }) =>
      api.post('auto_dispatch/unlock_schedules', payload.ids, {
        queryParams: new URLSearchParams({ run_id: payload.run_id }),
      }),
  })

  const useOrderLock = useMutation({
    mutationKey: ['orderLock'],
    mutationFn: (payload: { ids: string[]; run_id: string }) =>
      api.post('auto_dispatch/lock_orders', payload.ids, {
        queryParams: new URLSearchParams({ run_id: payload.run_id }),
      }),
  })

  const useOrderUnlock = useMutation({
    mutationKey: ['orderUnlock'],
    mutationFn: (payload: { ids: string[]; run_id: string }) =>
      api.post('auto_dispatch/unlock_orders', payload.ids, {
        queryParams: new URLSearchParams({ run_id: payload.run_id }),
      }),
  })

  const useRunAccept = useMutation({
    mutationKey: ['runAccept'],
    mutationFn: (id: string) => api.post('auto_dispatch/accept', { id }),
  })

  const useRunReject = useMutation({
    mutationKey: ['runReject'],
    mutationFn: (id: string) => api.post('auto_dispatch/reject', { id }),
  })

  const createNewRun = useMutation({
    mutationKey: ['createNewRun'],
    mutationFn: (payload: { date: string; shift: string }) => api.post('auto_dispatch/create_new_run', payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['autoDispatchOverview'],
      })
    },
  })

  const automatedDispatchCancel = useMutation({
    mutationKey: ['automatedDispatchCancel'],
    mutationFn: (id: string) => api.post('auto_dispatch/cancel', { id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['autoDispatchOverview'],
      })
    },
  })

  return {
    useAutoDispatchOverview,
    useAutoDispatchDetails,
    useScheduleLock,
    useScheduleUnlock,
    useOrderLock,
    useOrderUnlock,
    useRunAccept,
    useRunReject,
    createNewRun,
    automatedDispatchCancel,
    useAutoDispatchBuildingDetails,
  }
}
